import { useEffect, useState } from "react";

import { Heart, Pencil, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

import { RatingInput } from "./RatingInput.jsx";
import { Stars } from "./Stars.jsx";
import { authorOf, ratingOf, ratingOfVisit, reviewOf } from "../lib/reviews.js";
import { cn, fmtWon, todayISO } from "../lib/util.js";

const FIELD = "h-auto rounded-[7px] border-border bg-ground px-2.5 py-[7px] text-[13.5px] shadow-none";
const LABEL = "text-[12.5px] text-muted-foreground";

/** 리뷰 작성 · 수정 · 목록. 별점 하나가 필수고 나머지는 안 적어도 된다. */
export function Sheet({ store, reviews, onSave, onUpdate, onWant, onDeleteVisit, onClose }) {
  const [rating, setRating] = useState(0);
  const [menu, setMenu] = useState("");
  const [price, setPrice] = useState("");
  const [memo, setMemo] = useState("");
  /* 고치는 중인 리뷰의 id. null 이면 새로 쓰는 중이다 — **폼은 하나고 모드만 바뀐다.**
     수정용 폼을 따로 만들면 별점·메뉴·가격·한줄평의 라벨·검증·초기화가 두 벌이 되고,
     두 벌은 언젠가 갈라진다. 여기서 갈라지면 '수정하면 값이 하나 사라진다'가 된다. */
  const [editing, setEditing] = useState(null);

  const id = store?.id;

  const blank = () => {
    setRating(0); setMenu(""); setPrice(""); setMemo(""); setEditing(null);
  };
  useEffect(blank, [id]);   // eslint-disable-line react-hooks/exhaustive-deps -- 열 때마다 초안을 비운다

  const r = store ? reviewOf(reviews, store.id) : { want_to_go: false, visits: [] };
  const { score } = store ? ratingOf(reviews, store.id) : { score: 0 };
  const editVisit = editing != null ? r.visits.find((v) => v.id === editing) : null;

  /* 고치던 리뷰가 사라지면(옆에서 지웠거나 목록을 다시 받았거나) 모드를 푼다.
     안 그러면 이미 없는 행을 향한 '수정' 버튼이 남아서, 눌러도 아무 일이 안 일어난다. */
  useEffect(() => { if (editing != null && !editVisit) blank(); },
            [editing, editVisit]);   // eslint-disable-line react-hooks/exhaustive-deps

  /** 남긴 리뷰를 폼으로 끌어올린다. 빈 값은 빈 칸이지 "null" 이 아니다. */
  function startEdit(v) {
    setEditing(v.id);
    setRating(v.rating);
    setMenu(v.menu || "");
    setPrice(v.price != null ? String(v.price) : "");
    setMemo(v.memo || "");
  }

  function save() {
    const n = parseInt(price, 10);
    const visit = {
      rating,
      menu: menu.trim() || undefined,
      price: Number.isFinite(n) ? n : undefined,
      memo: memo.trim() || undefined,
    };
    /* 고칠 때는 시트를 안 닫는다. 새로 쓰는 것은 탭 한 번으로 끝나는 일이라 닫는 게
       맞지만(App.jsx 의 onSave), 고치러 온 사람은 고친 줄이 어떻게 바뀌었는지 보러
       온 것이다. 닫아버리면 확인하려고 시트를 다시 열어야 한다.
       날짜는 안 보낸다 — 방문한 날은 고치는 값이 아니다(mutations.gql). */
    if (editing != null) { onUpdate(store.id, editing, visit); blank(); return; }
    onSave(store.id, { date: todayISO(), ...visit });
  }

  return (
    <Dialog open={!!store} onOpenChange={(open) => { if (!open) onClose(); }}>
      {store && (
        <DialogContent
          id="sheet"
          showCloseButton={false}
          className="w-[min(440px,calc(100vw-24px))] max-w-none gap-4 rounded-[14px]
                     p-[20px_22px_22px] shadow-sheet"
        >
          {/* 네이티브 <dialog> 시절 form[method=dialog] 이 하던 닫기는 Radix 가 가져갔다.
              여기 남은 form 은 라벨-입력 묶음일 뿐이라, 엔터로 새로고침되지 않게 막는다. */}
          {/* min-w-0: 시트는 grid 라 안쪽 폼이 기본값(min-width:auto)이면 **내용이 폭을
              정한다** — 긴 글 한 줄이 폼을 모달 밖으로 밀어내고, 같은 폼 안의 입력칸도
              같이 끌려 나간다. 폭은 시트가 정하고 내용은 그 안에서 접히게 못박는다. */}
          <form
            className="flex min-w-0 flex-col gap-4"
            id="sheet-form"
            onSubmit={(e) => e.preventDefault()}
          >
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <DialogTitle id="sheet-name" className="text-lg tracking-[-.01em]">
                  {store.name}
                </DialogTitle>
                {/* 날짜는 '이 리뷰가 어느 날의 것인가'다. 새로 쓰면 오늘, 고치는
                    중이면 그 방문의 날짜 — 폼이 왜 채워져 있는지를 여기서 읽는다. */}
                <DialogDescription id="sheet-sub" className="mt-1 text-xs">
                  {editVisit
                    ? `${store.category || "기타"} · ${editVisit.date} 리뷰 고치는 중`
                    : `${store.category || "기타"} · ${todayISO()}`}
                </DialogDescription>
              </div>
              {/* 하트와 ×는 한 줄에 나란히 선 형제다. Radix 기본 닫기 버튼은 시트 모서리에
                  absolute(top-4 right-4)로 얹히는데, 하트는 흐름 안에 있어서 축이 10px
                  어긋나 있었다 — 시트 패딩이나 제목 크기를 건드릴 때마다 다시 틀어지는
                  정렬이다. 같은 상자(icon-sm)로 같은 행에 두면 정렬이 마크업으로 정해진다.
                  -mt-0.5 는 둘을 함께 제목 첫 줄에 맞추는 것이다. */}
              <div className="-mt-0.5 flex flex-none items-center gap-0.5">
                {/* 찜은 리뷰가 아니라 북마크다. 리뷰 폼(별점·메뉴·가격·한줄평·등록)
                    바깥으로 빼서 아이콘만 남겼다 — 지도의 점선 하트 마커가 이 값을 쓴다. */}
                <Button
                  type="button" variant="ghost" size="icon-sm" id="btn-want"
                  aria-pressed={r.want_to_go}
                  aria-label={r.want_to_go ? "찜 해제" : "찜하기"}
                  title={r.want_to_go ? "찜 해제" : "찜하기"}
                  /* hover 색을 두 상태 모두에 못박아야 한다. shadcn 의 ghost 변형이
                     hover:text-accent-foreground(= 흰색)를 들고 있어서, 안 덮으면 켜진 하트가
                     hover 순간 흰 채움 + 흰 글자가 되어 흰 시트 위에서 사라진다. */
                  className={cn("btn-want hover:bg-transparent",
                                r.want_to_go
                                  ? "text-primary hover:text-primary/70"
                                  : "text-muted-foreground hover:text-primary")}
                  onClick={() => onWant(store.id)}
                >
                  <Heart className={cn("size-[18px]", r.want_to_go && "fill-current")} />
                </Button>
                <DialogClose asChild>
                  <Button
                    type="button" variant="ghost" size="icon-sm" id="btn-close"
                    aria-label="닫기" title="닫기"
                    className="text-muted-foreground hover:bg-transparent hover:text-foreground"
                  >
                    <X className="size-[18px]" />
                  </Button>
                </DialogClose>
              </div>
            </div>

            <div className="rating-field flex items-center gap-3 rounded-[10px] bg-ground p-[10px_12px]">
              <span className={LABEL}>별점</span>
              <RatingInput value={rating} onChange={setRating} />
            </div>

            <div className="grid grid-cols-[52px_1fr] items-center gap-x-3 gap-y-2">
              {/* placeholder 는 예시가 아니라 안내문이다. 예전엔 '제육정식', '9000' 처럼
                  예시 값을 넣었는데, 흐린 글씨로 값이 적혀 있으면 이미 채워진 폼처럼 보였다.
                  '입력해주세요'는 값으로 오해될 수가 없어서 그 문제가 없다. */}
              <label className={LABEL} htmlFor="f-menu">메뉴</label>
              <Input id="f-menu" type="text" placeholder="메뉴를 입력해주세요." autoComplete="off"
                     className={FIELD} value={menu} onChange={(e) => setMenu(e.target.value)} />
              <label className={LABEL} htmlFor="f-price">가격</label>
              {/* type=number 가 아니라 text + 숫자만 남기기다. number 칸은 한글 IME 가 조합
                  중인 'ㅁ' 을 그대로 띄워놓고 value 는 '' 로 보고한다 — React 가 받는 값이
                  안 바뀌니 되돌려 지울 수도 없고, 글자만 칸에 남는다. 여기서 걸러 넣으면
                  숫자 아닌 건 애초에 칸에 들어가지 않는다. */}
              <Input id="f-price" type="text" inputMode="numeric" autoComplete="off"
                     placeholder="가격을 입력해주세요."
                     className={FIELD} value={price}
                     onChange={(e) => setPrice(e.target.value.replace(/\D/g, ""))} />
              <label className={LABEL} htmlFor="f-memo">한줄평</label>
              <Input id="f-memo" type="text" placeholder="한줄평을 입력해주세요." autoComplete="off"
                     className={FIELD} value={memo} onChange={(e) => setMemo(e.target.value)} />
            </div>

            {/* 별점만 필수다. 나머지는 비워도 등록된다 — 점수 하나 남기려고 들어온
                사람에게 메뉴·가격까지 받아내면 그 한 건도 안 남는다.
                고치는 중에는 '취소'가 옆에 선다. 폼 하나를 두 모드가 나눠 쓰므로,
                빠져나올 문이 없으면 고치다 만 값이 다음 등록에 딸려 들어간다. */}
            <div className="flex gap-2">
              {editing != null && (
                <Button type="button" variant="outline" id="btn-cancel-edit"
                        className="h-auto flex-none rounded-[10px] p-3 text-[15px] font-normal"
                        onClick={blank}>
                  <X className="size-4" />
                </Button>
              )}
              {/* w-full 이 아니라 flex-1 이다. 취소 버튼 옆에서 폭 100% 를 요구하면
                  그 버튼만큼 통째로 시트 밖으로 밀려난다 — 남는 폭을 받아쓴다.
                  혼자 있을 때(등록)는 결국 한 줄을 다 채우므로 모양은 그대로다. */}
              <Button type="button" id="btn-save" disabled={!rating}
                      className="h-auto min-w-0 flex-1 rounded-[10px] p-3 text-[15px] font-bold
                                 disabled:opacity-40"
                      onClick={save}>
                {editing != null ? "수정" : "등록"}
              </Button>
            </div>

            <div className="history flex flex-col gap-1.5 border-t border-border pt-3" id="history"
                 hidden={r.visits.length === 0}>
              {/* 카드에서 '리뷰 N건'을 눌러 여기 왔다. 같은 것을 같은 이름으로 부른다 —
                  누른 것이 '리뷰'였는데 열린 목록이 '방문 기록'이면 다른 걸 연 것처럼 읽힌다. */}
              <h3 className="m-0 flex items-baseline gap-2 text-xs font-normal tracking-[.1em]
                             text-muted-foreground">
                리뷰 {r.visits.length}건
                <span className="flex items-baseline gap-1.5 tracking-normal">
                  <b className="font-bold text-foreground">{score.toFixed(1)}</b>
                  <Stars score={score} className="text-[11px]" />
                </span>
              </h3>
              {/* 팀 전원의 리뷰가 쌓인다. 이름을 안 적으면 남의 한줄평을 내가 쓴 것으로
                  읽게 되고, 그 순간 목록 전체가 누구 말인지 모르는 글이 된다.
                  내 것은 이름을 강조색으로 둔다 — 훑다가 '어디까지가 내 기록인가'를 찾는 데
                  드는 시간이 이 목록에서 제일 아깝다.
                  지우기는 내 것에만 붙인다. 서버도 user_id 로 막고 있지만(mutations.gql),
                  누를 수 있는데 실패하는 버튼은 막는 게 아니라 속이는 것이다.

                  한 건이 두 줄이다 — 위는 날짜·별점·이름, 아래는 쓴 내용. 한 줄에 다
                  넣으면 내용 칸이 200px 도 안 남아서, 길게 쓴 리뷰일수록 덜 보인다.
                  긴 리뷰가 읽히라고 있는 목록에서 그건 거꾸로다.

                  목록이 길어지면 여기서만 스크롤한다. 시트 전체가 자라면 별점·등록
                  버튼이 화면 밖으로 밀려서, 리뷰를 읽다가 쓰려면 창을 닫아야 한다. */}
              {/* -mx-2 는 아래 줄들이 갖는 px-2 를 상쇄한다. 줄이 제 여백을 갖고
                  목록이 그만큼 넓어져야, 고치는 중인 줄의 배경이 글자보다 넓게
                  깔리면서도 가로로 넘치지 않는다 — 줄 쪽에 음수 마진을 주면 목록
                  안쪽 폭을 넘어서 가로 스크롤이 생기고 오른쪽 끝(×)이 잘린다. */}
              <div id="history-list"
                   className="-mx-2 flex max-h-[38dvh] flex-col gap-2 overflow-y-auto">
                {r.visits.map((v) => {
                  const facts = [v.menu, v.price ? fmtWon(v.price) : null]
                    .filter(Boolean).join(" · ");
                  return (
                    /* 고치는 중인 줄은 표시해둔다. 폼은 목록 위에 있어서, 표시가
                       없으면 어느 줄을 끌어올린 것인지 스크롤해 확인해야 한다.
                       여백(px-2 py-1)은 모든 줄이 늘 갖는다. 켜질 때만 주면 그 줄만
                       폭·높이가 달라져서, 연필을 누를 때마다 목록이 한 번 들썩인다. */
                    <div className={cn("visit flex flex-col gap-px px-2 py-1 text-[13px]",
                                       editing === v.id
                                         && "editing rounded-[8px] bg-primary-soft")}
                         key={v.id}>
                      <div className="flex items-baseline gap-[9px]">
                        <time className="flex-none text-[11.5px] text-muted-foreground">{v.date}</time>
                        <Stars score={ratingOfVisit(v)} className="vd flex-none text-[10px]" />
                        {/* 이름은 자르지 않는다. 길면 다음 줄로 내려간다 — 누가 남겼는지가
                            반쯤 잘려 '김…' 이 되면 이름을 붙인 뜻이 없다. */}
                        <span className={cn("va min-w-0 break-keep text-[11.5px]",
                                            v.mine ? "font-semibold text-primary"
                                                   : "text-muted-foreground")}>
                          {authorOf(v)}
                        </span>
                        {/* 고치기와 지우기는 한 쌍이라 나란히 둔다. 지우기가 오른쪽
                            끝이던 자리를 지키고 연필이 그 왼쪽에 붙는다 — 되돌릴 수
                            없는 쪽(×)이 바깥, 되돌릴 수 있는 쪽(연필)이 안쪽이다. */}
                        {v.mine && (
                          <>
                            <Button type="button" variant="ghost" size="icon-xs"
                                    aria-label="이 리뷰 수정" title="이 리뷰 수정"
                                    className="edit ml-auto size-auto p-0 px-1 leading-none
                                               text-muted-foreground hover:bg-transparent hover:text-primary"
                                    onClick={() => startEdit(v)}>
                              <Pencil className="size-3.5" />
                            </Button>
                            <Button type="button" variant="ghost" size="icon-xs" title="이 리뷰 삭제"
                                    className="del size-auto p-0 px-1 text-[15px] leading-none
                                               text-muted-foreground hover:bg-transparent hover:text-never"
                                    onClick={() => onDeleteVisit(store.id, v.id)}>
                              ×
                            </Button>
                          </>
                        )}
                      </div>
                      {/* 메뉴·가격은 값이라 가운뎃점으로 잇고, 한줄평은 문장이라 제 줄에 둔다.
                          셋을 한 줄로 이으면 접히는 자리에 '12,000원 ·' 처럼 구분점이
                          줄 끝에 매달린다 — 다음 값이 오는 줄 알고 읽다가 문장이 시작된다.

                          줄임표 없이 끝까지 보여준다. 대신 반드시 접혀야 한다 —
                          nowrap 이면 모달(440px)이 아니라 글자 길이가 폭을 정해서,
                          시트를 감싼 grid 트랙이 통째로 늘어나고 입력칸까지 밖으로 나간다.
                          break-keep 은 한글을 어절 단위로 끊고(글자 중간에서 안 자른다),
                          overflow-wrap:anywhere 는 띄어쓰기 없는 긴 덩어리(URL 같은)를
                          강제로 끊는다. 후자가 없으면 그런 한 덩어리가 다시 폭을 늘린다. */}
                      {facts && (
                        <div className="vm break-keep text-ink-2 [overflow-wrap:anywhere]">
                          {facts}
                        </div>
                      )}
                      {v.memo && (
                        <div className="vmemo break-keep text-ink-2 [overflow-wrap:anywhere]">
                          {v.memo}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </form>
        </DialogContent>
      )}
    </Dialog>
  );
}
